// filepath: apps/web/src/features/admin/templates/update/TemplateUpdatesPage.tsx
//
// FR-UI-ADM-12 AC-1 — list view of all pending template updates.
//
// Renders a table of TemplateUpdateRow entries with a per-row
// "View" button that deep-links to the diff view at
// /admin/templates/updates/:templateId?installed=&target=.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { ApiError } from "@/definitions/types";
import {
  classifyTemplateUpdateFetchError,
  useTemplateUpdates,
} from "./queries";
import type { TemplateUpdateRow } from "./types";

interface TemplateUpdatesPageProps {
  /**
   * Click handler invoked when the operator clicks "View" on a
   * row. The handler receives the templateId / installed /
   * target triple and is responsible for navigation.
   */
  onView?: (row: TemplateUpdateRow) => void;
}

export function TemplateUpdatesPage({
  onView,
}: TemplateUpdatesPageProps): React.ReactElement {
  const { t } = useTranslation();
  const list = useTemplateUpdates();

  if (list.isLoading) {
    return (
      <div
        className="p-6 text-sm text-muted-foreground"
        data-testid="template-updates-loading"
      >
        {t("common.loading")}
      </div>
    );
  }

  if (list.error) {
    const classified = classifyTemplateUpdateFetchError(list.error as ApiError);
    return (
      <div
        className="m-6 rounded border border-red-300 bg-red-50 p-4 text-sm text-red-700"
        role="alert"
        data-testid={`template-updates-${classified.kind}`}
      >
        {classified.message}
      </div>
    );
  }

  const rows = list.data?.updates ?? [];

  if (rows.length === 0) {
    return (
      <div
        className="rounded border border-border bg-background p-6 text-center text-sm text-muted-foreground"
        data-testid="template-updates-empty"
      >
        {t("admin.templates.update.list.empty")}
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6" data-testid="template-updates-page">
      <header>
        <h1 className="text-lg font-semibold">
          {t("admin.templates.update.title")}
        </h1>
      </header>

      <div
        className="overflow-x-auto rounded border border-border bg-background"
        data-testid="template-updates-table-wrapper"
      >
        <table
          className="w-full text-sm"
          aria-label={t("admin.templates.update.title")}
        >
          <thead>
            <tr className="border-b border-border bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-3 py-2">
                {t("admin.templates.update.list.column.template_id")}
              </th>
              <th className="px-3 py-2">
                {t("admin.templates.update.list.column.installed")}
              </th>
              <th className="px-3 py-2">
                {t("admin.templates.update.list.column.target")}
              </th>
              <th className="px-3 py-2">
                {t("admin.templates.update.list.column.conflicts")}
              </th>
              <th className="px-3 py-2">
                {t("admin.templates.update.list.column.irreversible")}
              </th>
              <th className="px-3 py-2 text-right">
                {t("admin.templates.registry.column.actions")}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={`${row.template_id}@${row.installed_version}->${row.target_version}`}
                className="border-b border-border last:border-0"
                data-testid="template-updates-row"
                data-template-id={row.template_id}
                data-installed={row.installed_version}
                data-target={row.target_version}
              >
                <td className="px-3 py-2 font-mono text-xs">
                  {row.template_id}
                </td>
                <td className="px-3 py-2 font-mono text-xs">
                  {row.installed_version}
                </td>
                <td className="px-3 py-2 font-mono text-xs">
                  {row.target_version}
                </td>
                <td className="px-3 py-2 text-xs">{row.total_conflicts}</td>
                <td className="px-3 py-2 text-xs">
                  {row.has_irreversible_changes ? "✓" : ""}
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    className="rounded bg-primary px-3 py-2 text-xs font-medium text-primary-foreground hover:bg-primary/90"
                    data-testid="template-updates-row-view"
                    onClick={() => onView?.(row)}
                  >
                    {t("admin.templates.update.list.row.view")}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
